/**
 * EstimateStore — the estimation feature's data-access wrapper.
 *
 * Strict layering: this is the ONLY estimation module that talks to the API,
 * and it does so exclusively through the {@link EngineeringVectorApi} facade
 * (`api.documents<Estimate>('estimates')` + `api.identityProvider`). It never
 * imports ApiClient or calls fetch. React hooks call this store; pages call the
 * hook.
 *
 * Persistence model: each estimate is one document with id `est_<uuid>`. The
 * renderable fields (title, roomId, templateId, templateVersion, createdBy,
 * createdAt, outputs[JSON], deleted) are mirrored onto the DDB pointer row's
 * index attributes so the list renders directly from the pointer rows —
 * WITHOUT hydrating S3 bodies (the S3 body carries the full inputs, needed only
 * on the detail/re-open view via {@link get}).
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; queries filter it out.
 */

import type { EngineeringVectorApi } from '../../api';
import type { DocumentStore } from '../../api';
import type { StringMap } from '../../api';
import type { ListMetaInfoQuery } from '../../api';
import type { Estimate, EstimateDraft, EstimateSummary } from './types';
import { estimateDocId, parseOutputsAttr } from './estimateModel';

export const ESTIMATES_COLLECTION = 'estimates';

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  title: 'title',
  roomId: 'roomId',
  templateId: 'templateId',
  templateVersion: 'templateVersion',
  createdBy: 'createdBy',
  createdAt: 'createdAt',
  outputs: 'outputs',
  deleted: 'deleted',
} as const;

export class EstimateStore {
  private readonly docs: DocumentStore<Estimate>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<Estimate>(ESTIMATES_COLLECTION);
  }

  /** Builds the queryable index attributes mirrored onto the pointer row. */
  private indexAttributes(e: Estimate): StringMap {
    return {
      [ROW.title]: e.title,
      [ROW.roomId]: e.roomId ?? '',
      [ROW.templateId]: e.templateId,
      [ROW.templateVersion]: String(e.templateVersion),
      [ROW.createdBy]: e.createdBy ?? '',
      [ROW.createdAt]: e.createdAt ?? '',
      [ROW.outputs]: JSON.stringify(e.outputs ?? {}),
      [ROW.deleted]: e.deleted ? 'true' : 'false',
    };
  }

  /** Projects a raw pointer row into a lightweight summary. */
  private toSummary(row: StringMap): EstimateSummary {
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      title: row[ROW.title] ?? '',
      roomId: row[ROW.roomId] ?? '',
      templateId: row[ROW.templateId] ?? '',
      templateVersion: Number(row[ROW.templateVersion] ?? '0') || 0,
      createdBy: row[ROW.createdBy] ?? '',
      createdAt: row[ROW.createdAt] ?? '',
      outputs: parseOutputsAttr(row[ROW.outputs]),
    };
  }

  /**
   * Lists all non-deleted estimate summaries. Filters to this collection
   * defensively (the underlying list is not collection-scoped).
   */
  async listSummaries(): Promise<EstimateSummary[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === ESTIMATES_COLLECTION &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toSummary(r));
  }

  /**
   * Lists a single room's non-deleted estimate summaries. Issues a
   * roomId-filtered listMetaInfo query, then re-filters client-side (collection
   * + roomId + not-deleted) so the result is correct even if the backend
   * ignores the filter expression.
   */
  async listByRoom(roomId: string): Promise<EstimateSummary[]> {
    if (!roomId) return [];
    const query: ListMetaInfoQuery = {
      filterExpression: '#roomId = :roomId AND #deleted <> :deleted',
      expressionAttributeNames: {
        '#roomId': ROW.roomId,
        '#deleted': ROW.deleted,
      },
      expressionAttributeValues: {
        ':roomId': roomId,
        ':deleted': 'true',
      },
    };
    const rows = await this.docs.list(query);
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === ESTIMATES_COLLECTION &&
          r[ROW.roomId] === roomId &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toSummary(r));
  }

  /** Loads a single estimate document by id (hydrates the S3 body / inputs). */
  async get(id: string): Promise<Estimate | undefined> {
    return this.docs.get(id);
  }

  /**
   * Creates a new estimate. Seeds id, createdBy (from identity) and createdAt,
   * and returns the saved document.
   */
  async create(draft: EstimateDraft): Promise<Estimate> {
    const identity = await this.api.identityProvider.getIdentity();
    const estimate: Estimate = {
      id: estimateDocId(),
      title: draft.title.trim(),
      roomId: draft.roomId?.trim() || undefined,
      templateId: draft.templateId,
      templateVersion: draft.templateVersion,
      inputs: draft.inputs,
      outputs: draft.outputs,
      createdBy: identity.alias,
      createdAt: new Date().toISOString(),
      deleted: false,
    };
    await this.docs.save(estimate.id, estimate, this.indexAttributes(estimate));
    return estimate;
  }

  /** Persists a full estimate document in place. */
  async update(estimate: Estimate): Promise<Estimate> {
    const updated: Estimate = { ...estimate, deleted: false };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
    return updated;
  }

  /**
   * Soft-deletes an estimate: fetches it, flags it deleted, and updates both
   * the S3 body and the pointer row so queries filter it out.
   */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: Estimate = { ...existing, deleted: true };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
  }
}
